import * as React from "react";
import { ArrowDown, ArrowUp, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useParseBusinessReportTemplateMutation } from "@/modules/snapshots/businessReports.api";

import { EMPTY_CHECK, SAMPLE_AUTHORING_TEMPLATE_URL, type CheckDraft } from "./authoringChecks";

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const parseErrorMessage = (error: unknown): string => {
  const detail = (error as { data?: { detail?: unknown } })?.data?.detail;
  return typeof detail === "string" && detail.trim() ? detail : "The file could not be read as a template.";
};

interface Props {
  checks: CheckDraft[];
  onChange: (checks: CheckDraft[]) => void;
  /** Called with the file's name (without extension) after a successful parse, to prefill the template name. */
  onFileName?: (name: string) => void;
  disabled?: boolean;
}

/**
 * Row editor for authoring templates: heading + prompt per check, add/remove/reorder, and
 * "Upload from file" which parses an xlsx/csv through the backend without storing anything.
 */
export const AuthoringChecksEditor: React.FC<Props> = ({ checks, onChange, onFileName, disabled = false }) => {
  const fileInput = React.useRef<HTMLInputElement>(null);
  const [parseTemplate, { isLoading: parsing }] = useParseBusinessReportTemplateMutation();
  const busy = disabled || parsing;

  const updateCheck = (index: number, patch: Partial<CheckDraft>) =>
    onChange(checks.map((check, checkIndex) => (checkIndex === index ? { ...check, ...patch } : check)));

  const move = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (target < 0 || target >= checks.length) return;
    const next = [...checks];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const openFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const parsed = await parseTemplate(file).unwrap();
      onChange(parsed.prompts.map(prompt => ({ heading: prompt.name, prompt: prompt.prompt })));
      onFileName?.(parsed.name);
    } catch (error) {
      toast.error(parseErrorMessage(error));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[#344054]">Checks ({plural(checks.length, "row", "rows")})</h3>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => fileInput.current?.click()} disabled={busy}>
            <Upload className="mr-1 h-4 w-4" /> {parsing ? "Reading…" : "Upload from file"}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.csv"
            aria-label="Template file"
            className="sr-only"
            onChange={event => {
              void openFile(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange([...checks, { ...EMPTY_CHECK }])}
            disabled={busy}
          >
            <Plus className="mr-1 h-4 w-4" /> Add row
          </Button>
        </div>
      </div>
      <p className="text-xs font-medium text-[#687386]">
        Each row is one check run over the document as tracked changes. A file needs two columns,{" "}
        <strong>Heading</strong> and <strong>Prompt</strong>;{" "}
        <a href={SAMPLE_AUTHORING_TEMPLATE_URL} download className="text-[#c88b43] underline">
          download the example
        </a>
        .
      </p>
      <ol className="space-y-2" aria-label="Template rows">
        {checks.map((check, index) => (
          <li
            key={check.id ?? index}
            className="grid gap-2 rounded-md border border-[#ddd5cb] bg-[#fffdfb] p-3 md:grid-cols-[1fr_2fr_auto]"
          >
            <Input
              aria-label={`Row ${index + 1} heading`}
              placeholder="Heading"
              value={check.heading}
              disabled={busy}
              onChange={event => updateCheck(index, { heading: event.target.value })}
            />
            <Textarea
              aria-label={`Row ${index + 1} prompt`}
              placeholder="Prompt"
              rows={2}
              className="min-h-0"
              value={check.prompt}
              disabled={busy}
              onChange={event => updateCheck(index, { prompt: event.target.value })}
            />
            <div className="flex items-start gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Move row ${index + 1} up`}
                onClick={() => move(index, -1)}
                disabled={busy || index === 0}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Move row ${index + 1} down`}
                onClick={() => move(index, 1)}
                disabled={busy || index === checks.length - 1}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove row ${index + 1}`}
                onClick={() => onChange(checks.filter((_, checkIndex) => checkIndex !== index))}
                disabled={busy}
              >
                <Trash2 className="h-4 w-4 text-danger" />
              </Button>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};
